import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import i18next from "i18next";
import { useTranslation } from "react-i18next";
import TreePopup from "./TreePopup";
import LevelContext from "../../../utils/UserLevel";

const getAccountsFromStorage = () => {
  try {
    const raw = localStorage.getItem("accounts");
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch (error) {
    return [];
  }
};

const getNodeName = (node, isArabic) => {
  if (isArabic) {
    return (
      node?.name_ar ||
      node?.accountNameAR ||
      node?.name_en ||
      node?.accountNameEN ||
      ""
    );
  }

  return (
    node?.name_en ||
    node?.accountNameEN ||
    node?.name_ar ||
    node?.accountNameAR ||
    ""
  );
};

const flattenOptions = (nodes, isArabic, list = [], level = 0) => {
  (Array.isArray(nodes) ? nodes : []).forEach((node, index) => {
    const accountNumber = String(
      node?.account_no || node?.accountNumber || node?.id || "",
    );
    const accountName = String(getNodeName(node, isArabic));

    if (accountNumber || accountName) {
      list.push({
        id: String(node?.id || accountNumber || index),
        accountNumber,
        accountName,
        raw: node,
        level: level + 1,
      });
    }

    if (Array.isArray(node?.childs) && node.childs.length) {
      flattenOptions(node.childs, isArabic, list, level + 1);
    }
  });

  return list;
};

const normalizeAccountType = (value) => {
  const normalized = String(value || "").toLowerCase();

  if (normalized === "sub") return "secondary";
  if (normalized === "secondary") return "secondary";
  if (normalized === "key") return "secondary";
  if (normalized === "primary") return "primary";
  if (normalized === "main") return "primary";

  return "";
};

const getNodeNumber = (node) =>
  String(node?.account_no || node?.accountNumber || node?.id || "");

const resolveNodeType = (node) => {
  const explicitType = normalizeAccountType(
    node?.account_type || node?.accountType,
  );

  if (explicitType) return explicitType;

  return Array.isArray(node?.childs) && node.childs.length > 0
    ? "primary"
    : "secondary";
};

const baseInputClass =
  "text-xs text-[#463977] py-1 w-full px-2 outline-none min-w-0 bg-white border-0";

const SelectorTree = ({
  value = "",
  label = "",
  headerTitle = "",
  onSelect,
  onManualNumberChange,
  data,
  title = "",
  placeholderNumber = "",
  placeholderName = "",
  disabled = false,
  allowManualNumberInput = false,
  invalid = false,
  withInlineSuggestions = false,
  inlineSuggestionsPosition = "top",
  compact = false,
  invalidMsg = "",
  allowedAccountTypes = [],
  blockedAccountNumbers = [],
  isAccountCenter = false,
  maxNodeLevel = null,
}) => {
  const [t] = useTranslation("global");
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [showInlineSuggestions, setShowInlineSuggestions] = useState(false);
  const [inlineSuggestionsStyle, setInlineSuggestionsStyle] = useState(null);

  const { chart_account_level_limit, cost_center_level_limit } = useContext(
    LevelContext
  ) || {};
  const levelLimit = useMemo(
    () =>
      maxNodeLevel !== null
        ? maxNodeLevel
        : isAccountCenter
          ? cost_center_level_limit
          : chart_account_level_limit,
    [maxNodeLevel, isAccountCenter, cost_center_level_limit, chart_account_level_limit]
  );

  const isArabic = i18next.dir(i18next.language) === "rtl";
  const valueText = String(value || "");
  const wrapperRef = useRef(null);

  const sourceData = useMemo(() => {
    if (Array.isArray(data) && data.length) return data;
    return getAccountsFromStorage();
  }, [data]);

  const flatOptions = useMemo(
    () => flattenOptions(sourceData, isArabic, []),
    [sourceData, isArabic],
  );

  const allowedTypesSet = useMemo(
    () =>
      new Set(
        (Array.isArray(allowedAccountTypes) ? allowedAccountTypes : [])
          .map((type) => normalizeAccountType(type))
          .filter(Boolean),
      ),
    [allowedAccountTypes],
  );
  const blockedAccountsSet = useMemo(
    () =>
      new Set(
        (Array.isArray(blockedAccountNumbers) ? blockedAccountNumbers : [])
          .map((number) => String(number || "").trim())
          .filter(Boolean),
      ),
    [blockedAccountNumbers],
  );
  const isSelectableNode = useCallback(
    (node) => {
      // Check level limit
      if (levelLimit !== null && levelLimit !== undefined && node.level !== undefined) {
        if (Number(node.level) !== Number(levelLimit)) {
          return false;
        }
      }

      if (
        allowedTypesSet.size > 0 &&
        !allowedTypesSet.has(resolveNodeType(node.raw || node))
      ) {
        return false;
      }
      if (blockedAccountsSet.size > 0) {
        const accountNumber = String(node?.accountNumber || getNodeNumber(node) || "").trim();
        if (blockedAccountsSet.has(accountNumber)) {
          return false;
        }
      }
      return true;
    },
    [allowedTypesSet, blockedAccountsSet, levelLimit],
  );

  const inlineSuggestions = useMemo(() => {
    const query = valueText.trim().toLowerCase();
    if (!query || !withInlineSuggestions || !allowManualNumberInput) return [];

    return flatOptions
      .filter((item) => {
        if (!isSelectableNode(item)) return false;

        const haystack =
          `${item.accountNumber} ${item.accountName}`.toLowerCase();

        return haystack.includes(query);
      })
      .slice(0, 10);
  }, [
    valueText,
    withInlineSuggestions,
    allowManualNumberInput,
    flatOptions,
    isSelectableNode,
  ]);

  const handleInlineSelect = (item) => {
    if (!isSelectableNode(item)) return;

    onSelect?.({
      id: item.id,
      account_no: item.accountNumber,
      name: item.accountName,
      raw: item.raw,
    });

    setShowInlineSuggestions(false);
  };

  const shouldShowInlineSuggestions =
    withInlineSuggestions &&
    allowManualNumberInput &&
    showInlineSuggestions &&
    valueText.trim().length > 0;

  const updateInlineSuggestionsPosition = useCallback(() => {
    if (!wrapperRef.current) return;

    const rect = wrapperRef.current.getBoundingClientRect();

    const width = Math.max(rect.width, 220);

    const maxLeft = Math.max(8, window.innerWidth - width - 8);

    const left = Math.max(8, Math.min(rect.left, maxLeft));

    const menuHeightEstimate = 220;

    const preferBottom = inlineSuggestionsPosition === "bottom";

    const hasBottomSpace =
      window.innerHeight - rect.bottom > menuHeightEstimate;

    const hasTopSpace = rect.top > menuHeightEstimate;

    let top = rect.bottom + 4;

    let transform = "none";

    if (preferBottom) {
      if (!hasBottomSpace && hasTopSpace) {
        top = rect.top - 4;
        transform = "translateY(-100%)";
      }
    } else if (hasTopSpace || !hasBottomSpace) {
      top = rect.top - 4;
      transform = "translateY(-100%)";
    }

    setInlineSuggestionsStyle({
      position: "fixed",
      top,
      left,
      width,
      transform,
      zIndex: 2147483647,
    });
  }, [inlineSuggestionsPosition]);

  useEffect(() => {
    if (!shouldShowInlineSuggestions) return;

    updateInlineSuggestionsPosition();

    window.addEventListener("resize", updateInlineSuggestionsPosition);

    window.addEventListener("scroll", updateInlineSuggestionsPosition, true);

    return () => {
      window.removeEventListener("resize", updateInlineSuggestionsPosition);
      window.removeEventListener(
        "scroll",
        updateInlineSuggestionsPosition,
        true,
      );
    };
  }, [shouldShowInlineSuggestions, updateInlineSuggestionsPosition, valueText]);

  const wrapperClass = compact
    ? `flex w-full overflow-hidden bg-white ${
        invalid ? "outline outline-1 outline-red-500" : ""
      }`
    : `flex w-full rounded-md overflow-hidden border bg-white ${
        invalid ? "border-red-500" : "border-[#8D82B6] border-opacity-55"
      }`;

  const inputHeightClass = compact ? "h-8" : "h-[34px]";

  const triggerHeightClass = compact ? "h-8" : "h-[34px]";
  const handleOpenPopup = useCallback(() => {
    if (disabled) return;
    setIsPopupOpen(true);
  }, [disabled]);

  return (
    <>
      <div className="relative" ref={wrapperRef}>
        {shouldShowInlineSuggestions &&
          inlineSuggestionsStyle &&
          createPortal(
            <div
              style={inlineSuggestionsStyle}
              className="rounded-md border border-[#8D82B6] border-opacity-55 bg-white shadow-md max-h-48 overflow-auto"
            >
              {inlineSuggestions.length > 0 ? (
                inlineSuggestions.map((item) => (
                  <button
                    key={`${item.id}-${item.accountNumber}`}
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => handleInlineSelect(item)}
                    className="w-full px-2 py-2 text-start flex items-center justify-end hover:bg-slate-100 border-b last:border-b-0 border-slate-200"
                  >
                    <div className="text-[12px] text-[#463977] truncate">
                      {item.accountName || "-"}
                    </div>

                    <span className="text-[#463977] text-[11px]">-</span>

                    <div className="text-[12px] text-[#261759]">
                      {item.accountNumber}
                    </div>
                  </button>
                ))
              ) : (
                <div className="px-2 py-2 text-[11px] text-[#463977]">
                  {t("common.noData")}
                </div>
              )}
            </div>,
            document.body,
          )}

        {headerTitle && (
          <h1 className="text-[#261759] font-normal mb-0.5 text-[12px]">
            {headerTitle}
          </h1>
        )}

        <div className={wrapperClass} onDoubleClick={handleOpenPopup}>
          <input
            readOnly={!allowManualNumberInput || disabled}
            type="text"
            min={0}
            value={value}
            placeholder={placeholderNumber}
            className={`${baseInputClass} ${inputHeightClass} text-center border-r !w-[30%] border-[#8D82B6] border-opacity-55`}
            dir="ltr"
            onFocus={() => {
              if (
                withInlineSuggestions &&
                allowManualNumberInput &&
                !disabled
              ) {
                setShowInlineSuggestions(true);
              }
            }}
            onBlur={() => {
              if (withInlineSuggestions) {
                setTimeout(() => setShowInlineSuggestions(false), 120);
              }
            }}
            onChange={(e) => {
              onManualNumberChange?.(e.target.value);

              if (withInlineSuggestions) {
                setShowInlineSuggestions(true);
              }
            }}
          />

          <input
            readOnly
            type="text"
            value={label}
            placeholder={placeholderName}
            className={`${baseInputClass} ${inputHeightClass} border-r !w-[70%] border-[#8D82B6] border-opacity-55`}
            dir={isArabic ? "rtl" : "ltr"}
          />

          <button
            type="button"
            disabled={disabled}
            onClick={() => setIsPopupOpen(true)}
            className={`${triggerHeightClass} flex border-s w-[10%] min-w-[36px] border-[#8D82B6] border-opacity-55 justify-center items-center text-xs text-[#261759] bg-white hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-60`}
          >
            ...
          </button>
        </div>

        <small className="text-red-400">{invalidMsg ? invalidMsg : ""}</small>
      </div>

      <TreePopup
        isOpen={isPopupOpen}
        onClose={() => setIsPopupOpen(false)}
        data={sourceData}
        title={title}
        selectedValue={value}
        allowedAccountTypes={allowedAccountTypes}
        blockedAccountNumbers={blockedAccountNumbers}
        isAccountCenter={isAccountCenter}
        maxNodeLevel={levelLimit}
        onSelect={(selected) => {
          onSelect?.(selected);
        }}
      />
    </>
  );
};

export default SelectorTree;
